import InlineTextInput from '@/components/ui/inline-text-input';
import { pdkAxios } from '@/lib/pdk-axios';
import type { Trade } from '@/models/types';
import { tradesStore } from '@/stores/trades-store';
import { useMutation } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';

const TradePostalCodeEditor = ({
  tradeId,
  initialPostalCode,
}: {
  tradeId: Trade['id'];
  initialPostalCode: string;
}) => {
  const [postalCode, setPostalCode] = useState(initialPostalCode);

  const updatePostalCodeMutation = useMutation({
    onMutate: async ({ postalCode }: { postalCode: string }) => {
      tradesStore.updateTrade(tradeId, {
        postal_code: postalCode,
      });
    },
    mutationFn: async ({ postalCode }: { postalCode: string }) => {
      const response = await pdkAxios.post(
        `/trades/${tradeId}/postal_code`,
        // /api/trades/:trade_id/postal_code
        {
          postal_code: postalCode,
        },
      );

      return response.data;
    },
    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating postal code', error, variables);
      // TODO: handle error by adding the original cdkId back to the store
    },
  });

  const handlePostalCodeChange = useCallback(
    debounce((newPostalCode: string) => {
      updatePostalCodeMutation.mutate({ postalCode: newPostalCode });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (postalCode === initialPostalCode) return;

    handlePostalCodeChange(postalCode);
  }, [postalCode]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCode(e.target.value);
  }, []);

  useEffect(() => {
    setPostalCode(initialPostalCode);
  }, [initialPostalCode]);

  return (
    <div className="flex flex-row gap-1 items-center justify-between">
      <div className="flex flex-row gap-1 items-center">
        <div className="text-sm text-gray-500 dark:text-white">Postal Code</div>
      </div>
      <InlineTextInput
        value={postalCode}
        onChange={onChange}
        placeholder="Enter postal code"
        className="text-right"
        wrapperClassName="gap-1"
      />
    </div>
  );
};

export default TradePostalCodeEditor;
